/**
 * TvApp — the Spot-TV role. Orchestrates the full TV lifecycle over the room-do
 * transport (zero XMPP):
 *
 *   setup     — enter the 8-char LONG_LIVED setup code -> pair -> provision.
 *   idle      — show the room name + a rotating share key (minted via /pair/code),
 *               connected to the room-do control plane as the `spot-tv` server.
 *   meeting   — host the Jitsi iframe; relay Remote commands into it via
 *               executeCommand and ack them; broadcast presence on every change.
 *
 * This mirrors spot-client's TV flow (`/setup` -> idle -> `/meeting`), but the
 * control plane is the per-room Durable Object, not the spot-prosody MUC.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CalendarEvent, MessageEnvelope, RoomOccupant, SpotTvPresence } from '@spaces/contracts';
import { COMMANDS, MESSAGES } from '@spaces/contracts';
import { getConfig } from '../config.js';
import { getDeviceId } from '../device-id.js';
import { log } from '../log.js';
import { meetingNameForRoom } from '../meeting-name.js';
import { JitsiMeetingFrame, type JitsiMeetingFrameHandle } from '../meeting/JitsiMeetingFrame.js';
import { PairingClient, PairingError } from '../transport/pairing-client.js';
import { RemoteControlService } from '../transport/RemoteControlService.js';
import { Button, Card, CodeInput, Logo, Screen, Shell, StatusLine } from '../ui/components.js';
import { TvIdle } from './TvIdle.js';
import { TvMeetingOverlay } from './TvMeetingOverlay.js';
import {
    clearSession,
    ensureFreshAccess,
    loadSession,
    saveSession,
    sessionFromPair,
    type TvSession
} from './session.js';

type Phase = 'loading' | 'setup' | 'provisioning' | 'idle' | 'meeting' | 'error';

/**
 * Share-key refresh lifecycle. The SHORT_LIVED share key the TV shows is re-minted
 * by a self-rescheduling timer that runs for the WHOLE TV session — idle AND in a
 * meeting — so the key a user reads off the screen is ALWAYS still valid. This
 * mirrors legacy spot, whose join-code refresh is tied to the control-plane
 * connection lifecycle (not the meeting): it re-mints ~5 min before the server's
 * declared expiry. We schedule the next re-mint from the server-returned
 * `expiresAt`, a comfortable lead before it lapses.
 */
const SHARE_KEY_REFRESH_LEAD_MS = 5 * 60 * 1000; // re-mint this long before expiry (matches spot)
const SHARE_KEY_MIN_REFRESH_MS = 30 * 1000;      // never schedule a re-mint sooner than this
const SHARE_KEY_RETRY_MS = 30 * 1000;            // after a mint failure, retry soon (never go stale)

/** How often the idle screen re-pulls the room's calendar agenda (it changes slowly). */
const AGENDA_REFRESH_MS = 5 * 60 * 1000;

/** Delay until the next re-mint: a lead before the server's declared expiry, floored. */
function nextShareKeyRefreshMs(expiresAt: string): number {
    const expMs = Date.parse(expiresAt);

    if (!Number.isFinite(expMs)) {
        // Server omitted/garbled the expiry — fall back to a fixed safe interval.
        return SHARE_KEY_REFRESH_LEAD_MS;
    }

    return Math.max(SHARE_KEY_MIN_REFRESH_MS, expMs - Date.now() - SHARE_KEY_REFRESH_LEAD_MS);
}

function defaultPresence(): SpotTvPresence {
    return {
        audioMuted: false,
        videoMuted: false,
        tileView: false,
        screensharing: false,
        needPassword: false,
        kicked: false,
        waitingForMeetingStart: false,
        volumeControlSupported: false,
        electron: false,
        isLocalModerator: false,
        view: 'home'
    };
}

/** Read a setup code from the URL (?setup=, ?llpc=, or ?code=). */
function setupCodeFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);

    return params.get('setup') ?? params.get('llpc') ?? params.get('code');
}

export function TvApp(): JSX.Element {
    const config = useMemo(() => getConfig(), []);
    const deviceId = useMemo(() => getDeviceId('tv'), []);
    // Pairing goes through the SAME-ORIGIN proxy (/api) the client Worker exposes,
    // because the default-deny pairing Worker sends no CORS headers for a direct
    // cross-origin browser fetch. room-do is reached by WS (no CORS) directly.
    const pairing = useMemo(() => new PairingClient('/api'), []);

    const [phase, setPhase] = useState<Phase>('loading');
    const [status, setStatus] = useState<string>('Starting…');
    const [codeInput, setCodeInput] = useState<string>('');
    const [session, setSession] = useState<TvSession | null>(null);
    const [shareKey, setShareKey] = useState<string | null>(null);
    const [meetingName, setMeetingName] = useState<string | null>(null);
    // Phone invites to dial into the meeting on join (the Remote's "Dial a number" /
    // "Join with ID + phone"). Jitsi's iframe `invite` fires these on videoConferenceJoined.
    const [meetingInvites, setMeetingInvites] = useState<{ type: 'phone'; number: string }[]>([]);
    const [presence, setPresence] = useState<SpotTvPresence>(defaultPresence());
    const [roster, setRoster] = useState<RoomOccupant[]>([]);
    // The room's upcoming meetings shown on the idle screen. `undefined` until the
    // first successful pull (no agenda card shown); `[]` = fetched-empty ("none").
    const [agenda, setAgenda] = useState<CalendarEvent[] | undefined>(undefined);

    const rcsRef = useRef<RemoteControlService | null>(null);
    const frameRef = useRef<JitsiMeetingFrameHandle | null>(null);
    const sessionRef = useRef<TvSession | null>(null);
    const presenceRef = useRef<SpotTvPresence>(defaultPresence());
    /**
     * The laptop currently doing a wireless screenshare (its room endpoint id). The
     * TV routes ALL outbound proxy signalling back to this endpoint — sidestepping
     * jid mapping (one screenshare at a time, as in spot). Set from the inbound
     * CLIENT_PROXY_MESSAGE, cleared when sharing stops.
     */
    const activeSharerRef = useRef<string>('');
    /** Pending share-key re-mint timer (self-rescheduling; only ever one at a time). */
    const shareKeyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /** Latest `mintShareKey`, so the self-rescheduling timer always calls the current one. */
    const mintShareKeyRef = useRef<() => void>(() => undefined);

    /** Merge a presence partial, broadcast it, and reflect it in the UI. */
    const updatePresence = useCallback((partial: Partial<SpotTvPresence>) => {
        const next = { ...presenceRef.current, ...partial };

        presenceRef.current = next;
        setPresence(next);
        rcsRef.current?.publishPresence(next);
    }, []);

    /** Handle a command relayed from a Remote: drive the meeting, ack back. */
    const onCommand = useCallback((id: string, command: string, payload: unknown) => {
        const rcs = rcsRef.current;

        if (!rcs) {
            return;
        }

        if (command === COMMANDS.GO_TO_MEETING) {
            const sess = sessionRef.current;
            const data = payload as { meetingName?: string; invites?: { type: 'phone'; number: string }[] } | undefined;
            const name = data?.meetingName ?? (sess ? meetingNameForRoom(sess.roomId) : null);

            if (name) {
                log.log('TV go-to-meeting', { name, invites: data?.invites?.length ?? 0 });
                setMeetingName(name);
                setMeetingInvites(Array.isArray(data?.invites) ? data.invites : []);
                setPhase('meeting');
                updatePresence({ view: 'meeting', inMeeting: name, waitingForMeetingStart: true });
                rcs.sendAck(id, true);
            } else {
                rcs.sendAck(id, false, 'no meeting name');
            }

            return;
        }

        const frame = frameRef.current;

        if (!frame) {
            rcs.sendAck(id, false, 'TV is not in a meeting');

            return;
        }

        const result = frame.applyCommand(command as never, payload);

        rcs.sendAck(id, result.ok, result.error);
    }, [updatePresence]);

    /**
     * Inbound wireless-screenshare proxy signalling from a sharing laptop: remember
     * the sharer (so replies route back to it) and pump the message into the meeting.
     */
    const onProxyMessage = useCallback((frame: MessageEnvelope) => {
        if (frame.message !== MESSAGES.CLIENT_PROXY_MESSAGE) {
            return;
        }

        activeSharerRef.current = frame.from;
        log.log('TV got screenshare proxy message', { from: frame.from });
        // Pass the sender id through: the iframe's ProxyConnectionService needs the
        // full { from, data } envelope or it ignores the message (`if (!from) return`).
        frameRef.current?.feedProxyMessage(frame.from, frame.data);
    }, []);

    /** Outbound proxy signalling: the iframe wants to reply to a sharer. */
    const onProxyConnectionEvent = useCallback((event: { to?: string; data?: unknown }) => {
        // The iframe emits { to, data }: `to` is the sharer to reply to, `data` is the
        // inner { iq }. Forward the INNER data (the sharer wraps it back as { from, data });
        // route by `to`, falling back to the last sharer we heard from.
        const to = event.to || activeSharerRef.current;
        const rcs = rcsRef.current;

        if (!to || !rcs) {
            return;
        }

        rcs.sendMessage({
            kind: 'message',
            message: MESSAGES.JITSI_MEET_UPDATE,
            from: deviceId,
            to,
            data: event.data
        });
    }, [deviceId]);

    /** Cancel any pending share-key re-mint (one timer at a time). */
    const clearShareKeyTimer = useCallback(() => {
        if (shareKeyTimerRef.current !== null) {
            clearTimeout(shareKeyTimerRef.current);
            shareKeyTimerRef.current = null;
        }
    }, []);

    /**
     * Mint (or re-mint) the rotating remote share key, then self-reschedule the next
     * re-mint a comfortable lead before THIS code's server-declared expiry — so the
     * displayed key is never the stale one. Runs through idle AND meeting; a manual
     * "New key" tap re-enters here and simply resets the same single timer.
     */
    const mintShareKey = useCallback(async () => {
        const sess = sessionRef.current;

        if (!sess) {
            return;
        }

        clearShareKeyTimer();

        try {
            const fresh = await ensureFreshAccess(sess, pairing);

            sessionRef.current = fresh;
            setSession(fresh);

            const minted = await pairing.mintShareKey(fresh.accessToken);

            setShareKey(minted.code);
            log.log('TV minted share key', { code: minted.code, expiresAt: minted.expiresAt });

            shareKeyTimerRef.current = setTimeout(
                () => mintShareKeyRef.current(),
                nextShareKeyRefreshMs(minted.expiresAt)
            );
        } catch (err) {
            log.error('mint share key failed', { error: String(err) });
            setStatus(`Could not mint a share key: ${(err as Error).message}`);
            // Retry soon: a transient mint failure must not let the on-screen key expire.
            shareKeyTimerRef.current = setTimeout(() => mintShareKeyRef.current(), SHARE_KEY_RETRY_MS);
        }
    }, [clearShareKeyTimer, pairing]);

    /**
     * Pull the room's calendar agenda via the client Worker's same-origin proxy
     * (-> roomkeeper `GET /rooms/:id/calendar`), Bearer = the TV's access token. A
     * fetch failure (e.g. a not-connected calendar surfaced as 5xx) is logged and
     * the last agenda is kept — the idle screen never breaks over a calendar hiccup.
     */
    const refreshAgenda = useCallback(async () => {
        const sess = sessionRef.current;

        if (!sess) {
            return;
        }

        const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC';

        try {
            const res = await fetch(
                `/api/rooms/${encodeURIComponent(sess.roomId)}/calendar?tzid=${encodeURIComponent(tzid)}`,
                { headers: { authorization: `Bearer ${sess.accessToken}` } }
            );

            if (!res.ok) {
                log.warn('TV agenda fetch failed', { status: res.status });

                return;
            }

            const body = await res.json() as { events?: CalendarEvent[] };

            setAgenda(body.events ?? []);
            log.log('TV agenda refreshed', { count: body.events?.length ?? 0, source: res.headers.get('x-calendar-source') });
        } catch (err) {
            log.warn('TV agenda fetch error', { error: String(err) });
        }
    }, []);

    /** Provision: connect the control plane as the TV, mint a share key, go idle. */
    const provision = useCallback(async (sess: TvSession) => {
        setPhase('provisioning');
        setStatus('Connecting to the room…');

        try {
            const fresh = await ensureFreshAccess(sess, pairing);

            sessionRef.current = fresh;
            setSession(fresh);
            saveSession(fresh);

            const rcs = new RemoteControlService({
                roomDoUrl: config.roomDoUrl,
                roomId: fresh.roomId,
                endpointId: deviceId,
                accessToken: fresh.accessToken,
                clientType: 'spot-tv',
                // The TV is a wall display: it must retry forever, re-minting its
                // access token from the refresh token before each attempt so a long
                // outage (or an expired access token) never permanently strands it.
                maxReconnectAttempts: Number.POSITIVE_INFINITY,
                refreshAccessToken: async () => {
                    const sess = sessionRef.current;

                    if (!sess) {
                        throw new Error('no session to refresh');
                    }

                    const next = await ensureFreshAccess(sess, pairing);

                    sessionRef.current = next;
                    setSession(next);

                    return next.accessToken;
                }
            });

            rcs.on('command', frame => onCommand(frame.id, frame.command, frame.payload));
            rcs.on('message', frame => onProxyMessage(frame));
            rcs.on('roster', occupants => setRoster(occupants));
            // A transient drop — the transport is auto-reconnecting (truthful now:
            // there is real reconnect logic behind this status).
            rcs.on('reconnecting', info => {
                log.warn('TV control plane reconnecting', info);
                setStatus('Reconnecting to the room…');
            });
            // Back online: re-publish current presence so any Remote resyncs (the room
            // may have dropped the cached presence while the TV was away).
            rcs.on('reconnected', () => {
                log.log('TV control plane reconnected');
                rcsRef.current?.publishPresence(presenceRef.current);
                setStatus('Ready');
            });
            // Terminal: only a server-intentional close (conflict / unauthorized)
            // reaches here, since network drops retry forever above.
            rcs.on('disconnect', info => {
                log.warn('TV control plane disconnected', info);
                setStatus('Disconnected from the room — reload to reconnect');
                setPhase('error');
            });

            await rcs.connect();
            rcsRef.current = rcs;

            presenceRef.current = { ...defaultPresence(), roomName: fresh.roomName, view: 'home' };
            setPresence(presenceRef.current);
            rcs.publishPresence(presenceRef.current);

            await mintShareKey();

            setPhase('idle');
            setStatus('Ready');
        } catch (err) {
            log.error('provision failed', { error: String(err) });
            setStatus(`Provisioning failed: ${(err as Error).message}`);
            setPhase('error');
        }
    }, [config.roomDoUrl, deviceId, mintShareKey, onCommand, onProxyMessage, pairing]);

    /** Pair with a setup code, then provision. */
    const doSetup = useCallback(async (code: string) => {
        const trimmed = code.trim();

        if (!trimmed) {
            return;
        }

        setPhase('provisioning');
        setStatus('Pairing this room…');

        try {
            const pair = await pairing.pair(trimmed, deviceId);
            const sess = sessionFromPair(pair);

            saveSession(sess);
            setSession(sess);
            // Strip a one-shot setup code from the URL once it has provisioned, so a
            // reload resumes the stored session (above) instead of re-pairing, and the
            // code doesn't linger in the address bar. No-op for manual code entry (the
            // URL carries no code then).
            if (setupCodeFromUrl()) {
                window.history.replaceState({}, '', window.location.pathname);
            }
            await provision(sess);
        } catch (err) {
            const message = err instanceof PairingError
                ? `${err.message} (${err.code})`
                : (err as Error).message;

            log.error('TV setup failed', { error: message });
            setStatus(`Setup failed: ${message}`);
            setPhase('setup');
        }
    }, [deviceId, pairing, provision]);

    // Bootstrap: honour a URL setup code FIRST, else resume a stored session, else prompt.
    //
    // The URL code must win over a stored session: an explicit `?llpc=` (the admin
    // console's "Open TV" preview, or any setup deep-link) means "provision THIS room
    // now". If we resumed the stored session first, opening a second room's TV in a
    // browser that has already been a TV would silently relaunch the FIRST room and
    // ignore the code. A genuinely-provisioned TV reloads WITHOUT a code (doSetup strips
    // it post-provision), so it still resumes its stored session cleanly.
    useEffect(() => {
        const urlCode = setupCodeFromUrl();

        if (urlCode) {
            setCodeInput(urlCode);
            void doSetup(urlCode);

            return;
        }

        const stored = loadSession();

        if (stored) {
            log.log('TV resuming stored session', { roomId: stored.roomId });
            void provision(stored);

            return;
        }

        setPhase('setup');
        setStatus('Enter the room setup code');
    }, []);

    // Point the self-rescheduling re-mint timer at the latest mintShareKey. The timer
    // itself is started/reset inside mintShareKey (kicked off by provision) and runs
    // for the whole session — idle AND in a meeting — so the key never goes stale.
    useEffect(() => {
        mintShareKeyRef.current = () => void mintShareKey();
    }, [mintShareKey]);

    useEffect(() => () => {
        clearShareKeyTimer();
        rcsRef.current?.disconnect();
    }, [clearShareKeyTimer]);

    // Pull the agenda when the TV is idle, then poll on a slow cadence. Stops while
    // in a meeting (the idle screen — and its agenda — isn't visible then).
    useEffect(() => {
        if (phase !== 'idle') {
            return undefined;
        }

        void refreshAgenda();
        const timer = setInterval(() => void refreshAgenda(), AGENDA_REFRESH_MS);

        return () => clearInterval(timer);
    }, [phase, refreshAgenda]);

    const leaveMeeting = useCallback(() => {
        frameRef.current = null;
        activeSharerRef.current = '';
        setMeetingName(null);
        setPhase('idle');
        updatePresence({ view: 'home', inMeeting: '', audioMuted: false, videoMuted: false });
    }, [updatePresence]);

    const resetTv = useCallback(() => {
        clearShareKeyTimer();
        rcsRef.current?.disconnect();
        rcsRef.current = null;
        clearSession();
        setSession(null);
        setShareKey(null);
        setCodeInput('');
        setPhase('setup');
        setStatus('Enter the room setup code');
    }, [clearShareKeyTimer]);

    // ---- Views ---------------------------------------------------------------

    if (phase === 'meeting' && session && meetingName) {
        return (
            <Screen role = "tv">
                <div className = "meeting-stage" data-testid = "tv-meeting">
                    <JitsiMeetingFrame
                        ref = { frameRef }
                        meetingDomain = { config.meetingDomain }
                        meetingTenant = { session.meetingTenant || config.meetingTenant }
                        externalApiSrc = { config.externalApiSrc }
                        meetingName = { meetingName }
                        invites = { meetingInvites }
                        jwt = { session.meetingJwt }
                        useJwt = { config.useMeetingJwt }
                        displayName = { session.roomName }
                        onPresence = { updatePresence }
                        onProxyConnectionEvent = { onProxyConnectionEvent }
                        onMeetingEnd = { leaveMeeting } />
                    <TvMeetingOverlay presence = { presence } />
                </div>
            </Screen>
        );
    }

    if (phase === 'idle' && session) {
        // The QR + branded label both use the configured pickup host (e.g. cast.8x8.vc),
        // which serves this same client worker — so scanning the QR opens the Remote
        // pre-filled and typing `<shareHost>/<code>` reaches the same app.
        const shareUrl = shareKey
            ? `https://${config.shareHost}/remote-control?key=${encodeURIComponent(shareKey)}`
            : null;

        return (
            <Screen role = "tv">
                <TvIdle
                    roomName = { session.roomName }
                    roomId = { session.roomId }
                    shareHost = { config.shareHost }
                    shareLabel = { shareKey ? `${config.shareHost}/${shareKey}` : null }
                    shareCode = { shareKey }
                    shareUrl = { shareUrl }
                    remoteCount = { roster.filter(o => o.clientType !== 'spot-tv').length }
                    agenda = { agenda } />
            </Screen>
        );
    }

    return (
        <Screen role = "tv">
            <Shell>
                <Card>
                    <div className = "stack">
                        <div className = "row">
                            <Logo />
                            <span className = "eyebrow">Room TV</span>
                        </div>

                        { phase === 'setup' && (
                            <>
                                <StatusLine tone = "info" text = { status } />
                                <CodeInput
                                    value = { codeInput }
                                    length = { 8 }
                                    onChange = { setCodeInput }
                                    onEnter = { () => void doSetup(codeInput) }
                                    autoFocus = { true }
                                    testId = "tv-setup-input" />
                                <Button label = "Set up this room" block = { true } onClick = { () => void doSetup(codeInput) } testId = "tv-setup-submit" />
                            </>
                        ) }

                        { (phase === 'loading' || phase === 'provisioning') && (
                            <StatusLine tone = "info" text = { status } />
                        ) }

                        { phase === 'error' && (
                            <>
                                <StatusLine tone = "err" text = { status } />
                                <Button label = "Start over" variant = "ghost" onClick = { resetTv } />
                            </>
                        ) }
                    </div>
                </Card>
            </Shell>
        </Screen>
    );
}
